import { memo, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, useMediaQuery } from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import MenuList from '../MenuList';
import LogoSection from '../LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';
import { themeColors } from '../../../views/ConstantColors';
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import { useDispatch, useSelector } from 'store';
import { openDrawer } from 'store/slices/menu';

const Sidebar = () => {
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { drawerType } = useConfig();

    const logo = useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    p: 2,
                    mx: 'auto',
                    backgroundColor: themeColors.primary.lighter,
                    borderBottom: `1px solid ${themeColors.grey[200]}`,
                    marginBottom: '8px' // Reduced from 24px to 8px
                }}
            >
                <LogoSection />
            </Box>
        ),
        []
    );

    const drawerContent = (
        <Box sx={{ mt: 0 }}>
            {' '}
            {/* Removed mt: 3 */}
            <MenuList />
        </Box>
    );

    const drawerSX = {
        paddingLeft: drawerOpen ? '16px' : 0,
        paddingRight: drawerOpen ? '16px' : 0,
        marginTop: drawerOpen ? '8px' : '8px', // Reduced from 24px/20px to 8px
        backgroundColor: themeColors.background.paper
    };

    const drawer = useMemo(
        () => (
            <>
                {matchDownMd ? (
                    <Box sx={drawerSX}>{drawerContent}</Box>
                ) : (
                    <PerfectScrollbar
                        component="div"
                        style={{
                            height: !matchUpMd ? 'calc(100vh - 56px)' : 'calc(100vh - 88px)',
                            ...drawerSX
                        }}
                    >
                        {drawerContent}
                    </PerfectScrollbar>
                )}
            </>
        ),
        [matchUpMd, drawerOpen, drawerType]
    );

    return (
        <Box
            component="nav"
            sx={{
                flexShrink: { md: 0 },
                width: matchUpMd ? drawerWidth : 'auto',
                '& .MuiDrawer-paper': {
                    borderRight: `2px solid ${themeColors.primary.main}`,
                    backgroundColor: themeColors.background.paper,
                    color: themeColors.grey[800],
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        borderColor: themeColors.primary.dark,
                        backgroundColor: themeColors.grey[50]
                    }
                }
            }}
            aria-label="mailbox folders"
        >
            {matchDownMd || (drawerType === LAYOUT_CONST.MINI_DRAWER && drawerOpen) ? (
                <Drawer
                    variant={matchUpMd ? 'persistent' : 'temporary'}
                    anchor="left"
                    open={drawerOpen}
                    onClose={() => dispatch(openDrawer(!drawerOpen))}
                    sx={{
                        '& .MuiDrawer-paper': {
                            mt: matchDownMd ? 0 : 11,
                            zIndex: 1099,
                            width: drawerWidth,
                            backgroundColor: themeColors.background.paper,
                            color: themeColors.grey[800],
                            borderRight: `2px solid ${themeColors.primary.main}`,
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                borderColor: themeColors.primary.dark,
                                backgroundColor: themeColors.grey[50]
                            }
                        }
                    }}
                    ModalProps={{ keepMounted: true }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {matchDownMd && logo}
                        <Box sx={{ mt: 1 }}>
                            {' '}
                            {/* Reduced from mt: 3 to mt: 1 */}
                            {drawer}
                        </Box>
                    </Box>
                </Drawer>
            ) : (
                <MiniDrawerStyled
                    variant="permanent"
                    open={drawerOpen}
                    sx={{
                        '& .MuiDrawer-paper': {
                            backgroundColor: themeColors.background.paper,
                            borderRight: `2px solid ${themeColors.primary.main}`,
                            '&:hover': {
                                borderColor: themeColors.primary.dark,
                                backgroundColor: themeColors.grey[50]
                            }
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {logo}
                        <Box sx={{ mt: 1 }}>
                            {' '}
                            {/* Reduced from mt: 3 to mt: 1 */}
                            {drawer}
                        </Box>
                    </Box>
                </MiniDrawerStyled>
            )}
        </Box>
    );
};

export default memo(Sidebar);
